import {
  arrow,
  autoUpdate,
  useFloating,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  safePolygon,
} from '@floating-ui/react';
import React from 'react';
import { TooltipOptions } from '../../Tooltip';

export function useTooltip({
  hasArrow = false,
  placement = 'top',
  open: controlledOpen,
  setControlledOpen,
  delay,
}: TooltipOptions = {}) {
  const arrowRef = React.useRef(null);

  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    // required to prevent loop with other usage of ResizeObserver https://floating-ui.com/docs/autoupdate#elementresize
    whileElementsMounted(referenceEl, floatingEl, update) {
      const cleanup = autoUpdate(referenceEl, floatingEl, update, {
        elementResize: false,
      });
      return cleanup;
    },
    middleware: [
      offset(5),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5,
      }),
      shift({ padding: 5 }),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const context = data.context;

  const hoverOptions: any = {
    move: false,
    //  allows the user to move the cursor off the reference element and towards the floating element without it closing
    handleClose: safePolygon(),
    enabled: controlledOpen == null,
  };

  // Set the delay to open the tooltip
  if (delay) {
    hoverOptions.delay = { open: delay };
  }
  const hover = useHover(context, hoverOptions);
  const focus = useFocus(context, {
    enabled: controlledOpen == null,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const interactions = useInteractions([hover, focus, dismiss, role]);

  return React.useMemo(
    () => ({
      open,
      setOpen,
      arrowRef,
      hasArrow,
      ...interactions,
      ...data,
    }),
    [open, hasArrow, setOpen, interactions, data]
  );
}
